import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInstagram, faFacebookF } from "@fortawesome/free-brands-svg-icons";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import logo from "../assets/hci4goodlogo.png";
import "../scss/_navBar.scss";

const NavBar = () => {
    const { t, i18n } = useTranslation();

    const toggleLanguage = () => {
        const newLang = i18n.language === "en" ? "fr" : "en";
        i18n.changeLanguage(newLang);
    };

    const ScreenWidthCheck = () => {
        const [isDesktop, setDesktop] = useState(window.innerWidth > 650);

        const updateMedia = () => {
            setDesktop(window.innerWidth > 650);
        };

        useEffect(() => {
            window.addEventListener("resize", updateMedia);
            return () => window.removeEventListener("resize", updateMedia);
        }, []);

        return (
            <>
                {isDesktop ? (
                    <NavBarDesktop toggleLanguage={toggleLanguage} t={t} />
                ) : (
                    <NavBarMobile toggleLanguage={toggleLanguage} t={t} />
                )}
            </>
        );
    };

    return <ScreenWidthCheck />;
};

const NavBarDesktop = ({ toggleLanguage, t }) => {
    return (
        <div className="outerContainer">
            <div className="logoContainer">
                <Logo />
            </div>
            <div className="navbarContainer">
                <TopNavbar toggleLanguage={toggleLanguage} />
                <hr />
                <BottomNavbar t={t} />
            </div>
        </div>
    );
};

const NavBarMobile = ({ toggleLanguage, t }) => {
    const [open, setOpen] = useState(false);

    const linksVisible = open ? (
        <MobileNavLinks t={t} open={open} setOpen={setOpen} />
    ) : (
        <></>
    );
    return (
        <div className="navBarContainer">
            <div className="topBarContainer">
                <Logo setOpen={setOpen} />
                <Icons />
            </div>
            <hr />
            <div className="bottomBarContainer">
                <EnFr toggleLanguage={toggleLanguage} />
                <HamburgerIcon open={open} setOpen={setOpen} />
            </div>
            <div className="linksContainer">{linksVisible}</div>
        </div>
    );
};

const TopNavbar = ({ toggleLanguage }) => {
    const { i18n } = useTranslation();

    return (
        <div className="navbarTopRow">
            <div className="navBarLeft">
                <div className="enFrButtonContainer">
                    <button className="enFrButton" onClick={toggleLanguage}>
                        {i18n.language === "en" ? "FR" : "EN"}
                    </button>
                </div>
            </div>
            <Icons />
        </div>
    );
};

const BottomNavbar = ({ t }) => {
    return (
        <div className="navbarBottomRow">
            <div className="navLinks">
                <ul>
                    <li>
                        <Link className="link" to="/">
                            {t("home")}
                        </Link>
                    </li>
                    <li>
                        <Link className="link" to="/AboutUs">
                            {t("aboutUs")}
                        </Link>
                    </li>
                    <li>
                        <Link className="link" to="/Career">
                            {t("career")}
                        </Link>
                    </li>
                    <li>
                        <Link className="link" to="/Research">
                            {t("blog")}
                        </Link>
                    </li>
                    <li>
                        <Link className="link" to="/Upload">
                            {t("upload")}
                        </Link>
                    </li>
                    <li>
                        <Link className="link" to="/Resources">
                            {t("resources1")}
                        </Link>
                    </li>
                </ul>
            </div>
            <div className="donateButtonContainer">
                <Link className="link" to="/Donate">
                    <button className="donate-button">{t("donate")}</button>
                </Link>
            </div>
        </div>
    );
};

const MobileNavLinks = ({ t, open, setOpen }) => {
    const onLinkClick = () => {
        setOpen(!open);
    };

    const linkVariants = {
        hidden: { opacity: 0, y: -50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                delay: 0.1,
                duration: 0.5,
            },
        },
    };
    const backgroundVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.5,
            },
        },
    };
    const dropDownVariants = {
        hidden: { height: 0 },
        visible: {
            height: "auto",
            transition: {
                duration: 0.3,
            },
        },
    };

    return (
        <>
            <motion.div
                className="background"
                initial="hidden"
                animate="visible"
                variants={backgroundVariants}
            ></motion.div>
            <motion.div
                className="navLinks"
                initial="hidden"
                animate="visible"
                variants={dropDownVariants}
            >
                <ul>
                    <motion.li
                        variants={linkVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover={{
                            scale: 1.2,
                            transition: { duration: 0.3 },
                        }}
                    >
                        <Link className="link" to="/" onClick={onLinkClick}>
                            {t("home")}
                        </Link>
                    </motion.li>
                    <motion.li
                        variants={linkVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover={{
                            scale: 1.2,
                            transition: { duration: 0.3 },
                        }}
                    >
                        <Link
                            className="link"
                            to="/AboutUs"
                            onClick={onLinkClick}
                        >
                            {t("aboutUs")}
                        </Link>
                    </motion.li>
                    <motion.li
                        variants={linkVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover={{
                            scale: 1.2,
                            transition: { duration: 0.3 },
                        }}
                    >
                        <Link
                            className="link"
                            to="/Career"
                            onClick={onLinkClick}
                        >
                            {t("career")}
                        </Link>
                    </motion.li>
                    <motion.li
                        variants={linkVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover={{
                            scale: 1.2,
                            transition: { duration: 0.3 },
                        }}
                    >
                        <Link
                            className="link"
                            to="/OurBlog"
                            onClick={onLinkClick}
                        >
                            {t("blog")}
                        </Link>
                    </motion.li>
                    <motion.li
                        variants={linkVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover={{
                            scale: 1.2,
                            transition: { duration: 0.3 },
                        }}
                    >
                        <Link
                            className="link"
                            to="/Contact"
                            onClick={onLinkClick}
                        >
                            {t("contact")}
                        </Link>
                    </motion.li>
                    <motion.li
                        variants={linkVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover={{
                            scale: 1.2,
                            transition: { duration: 0.3 },
                        }}
                    >
                        <Link
                            className="link"
                            to="/Resources"
                            onClick={onLinkClick}
                        >
                            {t("resources")}
                        </Link>
                    </motion.li>
                </ul>
            </motion.div>
        </>
    );
};

const Logo = ({ setOpen }) => {
    const onLinkClick = () => {
        setOpen(false);
    };
    return (
        <Link to="/" onClick={onLinkClick}>
            <img className="logo" src={logo} alt="Logo" />
        </Link>
    );
};

const Icons = () => {
    return (
        <div className="IconContainer">
            <div className="socialLinks">
                <motion.a
                    href="https://www.instagram.com/hci_4_good/"
                    whileHover={{ scale: 1.2 }}
                >
                    <FontAwesomeIcon icon={faInstagram} />
                </motion.a>
                <motion.a
                    href="https://www.facebook.com/profile.php?id=61577992934450"
                    whileHover={{ scale: 1.2 }}
                >
                    <FontAwesomeIcon icon={faFacebookF} />
                </motion.a>
                <motion.a
                    href="mailto:hci4goodresearch@gmail.com"
                    whileHover={{ scale: 1.2 }}
                >
                    <FontAwesomeIcon icon={faEnvelope} />
                </motion.a>
            </div>
        </div>
    );
};

const EnFr = ({ toggleLanguage }) => {
    const { i18n } = useTranslation();

    return (
        <div className="enFrButtonContainer">
            <button className="enFrButton" onClick={toggleLanguage}>
                {i18n.language === "en" ? "FR" : "EN"}
            </button>
        </div>
    );
};

const HamburgerIcon = ({ open, setOpen }) => {
    const size = 20;
    const lines = 1;

    const [animation] = useState([
        {
            initial: { y: 0 },
            opened: {
                rotate: -45,
                y: size / lines - size / 2,
            },
        },
        {
            initial: { y: (size - lines) / 2 },
            opened: { rotate: 45 },
        },
        {
            initial: { y: size - lines },
            opened: {
                rotate: 45,
                y: size / lines - size / 2,
            },
        },
    ]);

    const onContainerClick = () => setOpen(!open);

    return (
        <motion.div className="hamburgerMenu" onClick={onContainerClick}>
            {animation.map((animation, index) => (
                <motion.div
                    key={index}
                    className="line"
                    style={{ height: 4 }}
                    variants={animation}
                    initial="initial"
                    animate={open ? "opened" : "initial"}
                />
            ))}
        </motion.div>
    );
};

export default NavBar;
